import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Typography, Divider, Spin, Empty, Button, Space, message } from 'antd';
import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
import { getPublicReport, generatePublicPDF, downloadPublicPDF } from '../../services/reportsService';

const { Title, Text, Paragraph } = Typography;

const SharedReportView = () => {
    const { reportId: shareToken } = useParams();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [generatingPDF, setGeneratingPDF] = useState(false);

    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            try {
                const response = await dispatch(getPublicReport(shareToken));
                
                if (response.success && response.data) {
                    setReport(response.data);
                } else {
                    setError(response.message || "Report not found or has expired.");
                }
            } catch (err) {
                console.error("Error fetching shared report:", err);
                setError("Unable to load report. It may be private or the link has expired.");
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [shareToken, dispatch]);

    const handleDownloadPDF = async () => {
        if (!shareToken) return;
        
        setGeneratingPDF(true);
        message.loading({ content: 'Preparing your PDF...', key: 'pdf_loading' });

        try {
            const response = await dispatch(generatePublicPDF(shareToken));
            const pdfId = response?.pdf_id || response?.data?.pdf_id;

            if (!pdfId) {
                throw new Error("No PDF ID received");
            }

            // Polling for PDF readiness
            const checkStatus = async () => {
                try {
                    const statusRes = await dispatch(downloadPublicPDF(shareToken, pdfId));
                    const pdfUrl = statusRes?.download_url || statusRes?.data?.download_url;

                    if (pdfUrl) {
                        message.success({ content: 'PDF ready! Downloading...', key: 'pdf_loading', duration: 2 });
                        const link = document.createElement('a');
                        link.href = pdfUrl;
                        link.download = statusRes?.file_name || 'report.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setGeneratingPDF(false);
                    } else if (statusRes?.status === 'failed') {
                        message.error({ content: 'PDF generation failed.', key: 'pdf_loading', duration: 3 });
                        setGeneratingPDF(false);
                    } else {
                        // Retry after 2 seconds
                        setTimeout(checkStatus, 2000);
                    }
                } catch (err) {
                    console.error("Polling error:", err);
                    message.error({ content: 'Error checking PDF status.', key: 'pdf_loading', duration: 3 });
                    setGeneratingPDF(false);
                }
            };

            setTimeout(checkStatus, 2000);

        } catch (err) {
            console.error("PDF generation error:", err);
            message.error({ content: 'Failed to generate PDF.', key: 'pdf_loading', duration: 3 });
            setGeneratingPDF(false);
        }
    };

    if (loading) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>Loading Report...</Text>
            </div>
        );
    }

    if (error || !report) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Empty 
                    description={error || "Report not found"} 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Button type="primary" onClick={() => window.location.href = 'https://www.floordetective.com'}>
                        Go to Floor Detective
                    </Button>
                </Empty>
            </div>
        );
    }

    // Parse Settings
    let parsedSettings = {};
    if (typeof report.cover_settings === 'string') {
        try { parsedSettings = JSON.parse(report.cover_settings); } catch (e) {}
    } else {
        parsedSettings = report.cover_settings || {};
    }

    const showCoverPhoto = (parsedSettings.show_cover_photo ?? parsedSettings.showCoverPhoto ?? true) === true;
    const showCompanyLogo = (parsedSettings.show_company_logo ?? parsedSettings.showCompanyLogo ?? true) === true;
    const showCompanyName = (parsedSettings.show_company_name ?? parsedSettings.showCompanyName ?? true) === true;
    const showCreatedDate = (parsedSettings.show_created_date ?? parsedSettings.showCreatedDate ?? true) === true;
    const showPhotoCount = (parsedSettings.show_photo_count ?? parsedSettings.showPhotoCount ?? true) === true;
    const showClaimNumber = (parsedSettings.show_claim_number ?? parsedSettings.showClaimNumber ?? true) === true;
    const showConclusion = report.show_conclusion ?? true;

    return (
        <div style={{ backgroundColor: '#f4f7f9', minHeight: '100vh', padding: '0 0 40px 0' }}>
            
            {/* Sticky Header */}
            <div style={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 1000, 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                backdropFilter: 'blur(8px)',
                borderBottom: '1px solid #e1e8ed',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                padding: '12px 20px'
            }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div>
                            <div style={{ fontWeight: 600,textTransform: 'capitalize', fontSize: '24px', color: '#1a1a1a', lineHeight: 1.2 }}>{report.title}</div>
                            <div style={{ fontSize: '15px', color: '#8c8c8c' }}>Powered by <span style={{ color: '#0a497b', fontWeight: 600 }}>Floor Detective</span></div>
                        </div>
                    </div>
                    <Button 
                        type="primary"
                        icon={generatingPDF ? <LoadingOutlined /> : <FilePdfOutlined />} 
                        size="middle"
                        onClick={handleDownloadPDF}
                        loading={generatingPDF}
                        style={{ borderRadius: '6px', fontWeight: 600,borderColor: '#0a497b', backgroundColor: '#0a497b' }}
                    >
                        Download PDF
                    </Button>
                </div>
            </div>

            <div style={{ maxWidth: '1000px', margin: '40px auto 0 auto', padding: '0 20px' }}>
                
                {/* Cover Page Section */}
                <Card style={{ 
                    marginBottom: '32px', 
                    borderRadius: '12px', 
                    border: '1px solid #e1e8ed', 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    textAlign: 'center',
                    padding: '0px',
                    overflow: 'hidden',
                    backgroundColor: '#fff'
                }}>
                    <div style={{ padding: '40px 30px 30px' }}>
                        {/* Logo / Branding */}
                        {showCompanyLogo && (
                            <div style={{ marginBottom: '24px' }}>
                                {report.company_data?.company_logo ? (
                                    <img 
                                        src={report.company_data.company_logo} 
                                        alt="Company Logo" 
                                        style={{ maxWidth: '160px', maxHeight: '70px', objectFit: 'contain' }} 
                                    />
                                ) : (
                                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#0a497b', letterSpacing: '0px' }}>
                                        FLOOR DETECTIVE
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Inspector & Company Details */}
                        <div style={{ marginBottom: '24px' }}>
                            {report.company_data?.inspector_name && (
                                <Text style={{ fontSize: '17px', fontWeight: 600, color: '#595959', display: 'block', marginBottom: '2px', textTransform: 'capitalize' }}>
                                    {report.company_data.inspector_name}
                                </Text>
                            )}
                            {showCompanyName && (
                                <Text strong style={{ fontSize: '20px', color: '#0a497b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                                    {report.company_data?.company_name || ''}
                                </Text>
                            )}
                        </div>

                        {/* Report Title */}
                        <Title level={1} style={{ fontSize: '32px', fontWeight: 600, color: '#1a1a1a', marginBottom: '16px', letterSpacing: '-0.5px', lineHeight: 1.2, textTransform: 'capitalize' }}>
                            {report.title}
                        </Title>

                        {/* Project Address Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <div style={{ 
                                display: 'inline-block', 
                                padding: '6px 16px', 
                                backgroundColor: '#f9f9f9', 
                                borderRadius: '8px',
                                border: '1px solid #f0f0f0'
                            }}>
                                <Text style={{ fontSize: '15px', color: '#434343', fontWeight: 400, display: 'block' }}>
                                    {[
                                        report.project?.address,
                                        report.project?.city,
                                        report.project?.state,
                                        report.project?.postal_code
                                    ].filter(Boolean).join(', ') || 'N/A'}
                                </Text>
                            </div>
                        </div>

                        {/* Claim Number Block */}
                        {showClaimNumber && report.claim_number && (
                            <div style={{ marginBottom: '32px', borderTop: '1px solid #f0f0f0', paddingTop: '20px', display: 'inline-block', minWidth: '240px' }}>
                                <Text type="secondary" style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: '13px', fontWeight: 600, color: '#8c8c8c' }}>
                                    Claim Number
                                </Text>
                                <div style={{ fontSize: '24px', fontWeight: 600, color: '#0a497b', marginTop: '2px' }}>
                                    {report.claim_number}
                                </div>
                            </div>
                        )}

                        {/* Date & Meta Info */}
                        {(showCreatedDate || showPhotoCount) && (
                            <div>
                                <div style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '24px', 
                                    padding: '10px 32px', 
                                    border: '1px solid #eee', 
                                    borderRadius: '6px',
                                    backgroundColor: '#fff',
                                }}>
                                    {showCreatedDate && (
                                        <div style={{ textAlign: 'left' }}>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', fontWeight: 600, color: '#7c7c7c', marginBottom: '1px' }}>Date</Text>
                                            <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>{new Date(report.created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>
                                        </div>
                                    )}
                                    {showCreatedDate && showPhotoCount && (
                                        <Divider type="vertical" style={{ height: '32px', backgroundColor: '#f0f0f0' }} />
                                    )}
                                    {showPhotoCount && (
                                        <div style={{ textAlign: 'left' }}>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase', fontWeight: 600, color: '#7c7c7c', marginBottom: '1px' }}>Photos</Text>
                                            <Text strong style={{ fontSize: '14px', fontWeight: 500 }}>{report.sections?.reduce((acc, s) => acc + (s.photos?.length || 0), 0)} Photos</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Banner Cover Image - Rebalanced Width & Height */}
                    {showCoverPhoto && report.cover_photo && (
                        <div style={{ 
                            padding: '0 20px 40px', 
                            backgroundColor: '#fff', 
                            textAlign: 'center' 
                        }}>
                            <img 
                                src={report.cover_photo.image_url} 
                                alt="Cover Photo" 
                                style={{ 
                                    width: '80%', 
                                    height: '440px',
                                    display: 'block', 
                                    margin: '0 auto',
                                    objectFit: 'cover', 
                                    objectPosition: 'center',
                                    borderRadius: '16px',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                                    border: '1px solid #f0f0f0'
                                }}
                            />
                        </div>
                    )}
                </Card>

                <Title level={3} style={{ marginBottom: '24px', color: '#1a1a1a', fontWeight: 600 }}>Report Sections</Title>

                {/* Report Content */}
                {report.sections && report.sections.map((section, index) => (
                    <div key={section.id} style={{ marginBottom: '48px' }}>
                        <div style={{ 
                            backgroundColor: '#0a497b', 
                            color: 'white', 
                            padding: '12px 24px', 
                            borderRadius: '12px 12px 0 0',
                            fontWeight: 600,
                            fontSize: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            Section {index + 1}: {section.title}
                        </div>
                        <div style={{ 
                            backgroundColor: 'white', 
                            padding: '32px', 
                            border: '1px solid #e1e8ed', 
                            borderTop: 'none',
                            borderRadius: '0 0 12px 12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
                        }}>
                            {section.summary && (
                                <div 
                                    className="photoreport-rich-content"
                                    style={{ 
                                        marginBottom: '40px', 
                                        fontSize: '16px', 
                                        color: '#434343', 
                                        backgroundColor: '#f9f9f9', 
                                        padding: '20px', 
                                        borderRadius: '8px', 
                                        borderLeft: '6px solid #0a497b',
                                        lineHeight: 1.6
                                    }}
                                    dangerouslySetInnerHTML={{ __html: section.summary }}
                                />
                            )}

                            <Space direction="vertical" size={40} style={{ width: '100%' }}>
                                {section.photos && section.photos.map((photo, pIndex) => {
                                    const imageUrl = photo.inspection_image?.image_url || photo.image_url || photo.url;
                                    const thumbUrl = photo.inspection_image?.thumb_url || imageUrl;
                                    return (
                                        <div key={photo.id} style={{ 
                                            backgroundColor: '#fff', 
                                            borderRadius: '16px', 
                                            overflow: 'hidden', 
                                            border: '1px solid #e1e8ed',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                                        }}>
                                            <Row gutter={0}>
                                                <Col xs={24} md={12}>
                                                    <img 
                                                        src={imageUrl} 
                                                        alt={photo.description || 'Report Photo'} 
                                                        style={{ width: '100%', height: '400px', display: 'block', objectFit: 'cover' }}
                                                    />
                                                </Col>
                                                <Col xs={24} md={12} style={{ padding: '32px' }}>
                                                    <div style={{ 
                                                        display: 'inline-block', 
                                                        backgroundColor: '#0a497b', 
                                                        color: 'white', 
                                                        padding: '4px 16px', 
                                                        borderRadius: '6px', 
                                                        fontSize: '13px', 
                                                        fontWeight: 700,
                                                        marginBottom: '20px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        PHOTO #{pIndex + 1}
                                                    </div>
                                                    
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Project:</div>
                                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>{report.project?.name || report.title}</div>
                                                    </div>

                                                    <div style={{ marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Date:</div>
                                                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a1a' }}>
                                                            {new Date(photo.inspection_image?.created_at || photo.captured_at || report.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: '2-digit', year: 'numeric' })}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Description:</div>
                                                        <Paragraph style={{ color: '#1f1f1f', fontSize: '16px', margin: 0, lineHeight: '1.7' }}>
                                                            {photo.inspection_image?.description || photo.description || "No description provided."}
                                                        </Paragraph>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })}
                            </Space>
                        </div>
                    </div>
                ))}

                {/* Conclusion */}
                {showConclusion && report.conclusion && (
                    <Card style={{ marginBottom: '60px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                        <div style={{ backgroundColor: '#0a497b', padding: '12px 24px', color: 'white', fontWeight: 700, fontSize: '18px' }}>
                            Conclusion
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div 
                                className="photoreport-rich-content"
                                style={{ fontSize: '16px', lineHeight: '1.8', color: '#434343' }}
                                dangerouslySetInnerHTML={{ __html: report.conclusion }}
                            />
                        </div>
                    </Card>
                )}

                {/* Footer Disclaimer & Socials */}
                <div style={{ textAlign: 'center', padding: '40px 0 60px 0', borderTop: '1px solid #e1e8ed' }}>
                    <div style={{ color: '#0a497b', fontWeight: 500, fontSize: '22px', marginBottom: '4px', letterSpacing: '-0.5px' }}>FLOOR DETECTIVE</div>
                    <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '20px' }}>
                        &copy; {new Date().getFullYear()} All Rights Reserved.
                    </Text>
                    
                    {/* Modern, Subtle Social Links */}
                    <Space size={16} align="center">
                        {[
                            { link: 'https://twitter.com/floor_detective/', img: 'https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055501/icons8-twitter-24.png', alt: 'Twitter' },
                            { link: 'https://www.instagram.com/floor_detective/', img: 'https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28065620/icons8-instagram-26.png', alt: 'Instagram' },
                            { link: 'https://www.linkedin.com/company/floor-detective/', img: 'https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055505/icons8-linkedin-24.png', alt: 'LinkedIn' },
                            { link: 'https://www.facebook.com/floordetective1', img: 'https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/06/28055438/icons8-facebook-24.png', alt: 'Facebook' }
                        ].map((social, index) => (
                            <a key={index} href={social.link} target="_blank" rel="noopener noreferrer" style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '36px',
                                height: '36px',
                                backgroundColor: '#f0f4f8',
                                borderRadius: '50%',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                            onMouseOver={(e) => Object.assign(e.currentTarget.style, { backgroundColor: '#0a497b', transform: 'translateY(-3px)' })}
                            onMouseOut={(e) => Object.assign(e.currentTarget.style, { backgroundColor: '#f0f4f8', transform: 'translateY(0)' })}
                            >
                                <img src={social.img} alt={social.alt} style={{ width: '18px', height: '18px', objectFit: 'contain', filter: 'grayscale(100%) opacity(70%)', transition: 'all 0.3s ease' }} 
                                onMouseOver={(e) => Object.assign(e.currentTarget.style, { filter: 'brightness(0) invert(1)' })}
                                onMouseOut={(e) => Object.assign(e.currentTarget.style, { filter: 'grayscale(100%) opacity(70%)' })}
                                />
                            </a>
                        ))}
                    </Space>
                </div>
            </div>

            <style>{`
                @media (max-width: 768px) {
                    .hide-on-mobile {
                        display: none;
                    }
                }
                .ant-card {
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default SharedReportView;

